import { Command, CommandsRegistry, ICommand, ICommandCanExecuteReturnValue, ICommandContext, ICommandContextUser, ICommandService, UndoableCommand } from '../../commands/common/commands.js';
import { IInstantiationService } from '../../instantiation/common/instantiation.js';
import { IUndoRedoElement } from '../../undo/common/action.js';
import { Emitter } from '../../../base/common/events.js';
import { isHierarchyNode, IHierarchyNode, IHierarchyService } from './hierarchy.js';
import { HierarchyNode, internalRetainNodeForUndo } from './hierarchyService.js';
import { ILogService } from '../../log/common/log.js';
import { ServicesAccessor } from '../../instantiation/common/instantiation.js';
import { safeStringify } from '../../../base/common/objects.js';
import { IClipboardService } from '../../clipboard/common/clipboardService.js';
import { ContextMenuContext, isContextMenuContext } from '../../contextview/common/context.js';
import { MenuId, MenuRegistry } from '../../actions/common/actions.js';
import { IOperationService, opTargetFor, originFromContext } from '../../../editor/services/operations/common/operations.js';
import { IChangesetService } from '../../../editor/services/changeset/common/changeset.js';
import { INotificationService } from '../../notifications/common/notifications.js';
import { IAnalyticsService } from '../../analytics/common/analytics.js';
import { IStorageService, StorageScope, StorageTarget } from '../../storage/common/storage.js';
import { Euler, Matrix4, Quaternion, Vector3 } from 'three';
import { aiTargetFor, parseAiTarget, resolveAiTarget, type AiTarget, type TargetResolverLike } from '../../ai/common/target.js';
import { IObjectTargetService, type OverTarget } from '../../../editor/services/project/common/documentTarget.js';
import { formatEulerDegrees, formatMatrix4, formatQuaternion, formatTransformBlock, formatUnityTransformClipboard, formatUnityVector3, formatVector3 } from '../../../base/common/transformFormat.js';



export interface SelectNodeCommandArgs {
	/** SESSION-INTERNAL: a live node or its hierarchy id (UI callers) */
	node?: IHierarchyNode | null | string | number;
	/** the model-facing shape (1h): a document target from a search */
	target?: AiTarget | string;
	options?: {
		multiSelect?: boolean;
		toggle?: boolean;
		rangeSelect?: boolean;
	};
}

export interface SelectionModifierKeys {
	readonly ctrlKey?: boolean;
	readonly metaKey?: boolean;
	readonly shiftKey?: boolean;
}

/** Viewport objects have no linear range, so every standard selection modifier
 *  toggles the picked object in the current selection. */
export function toggleSelectionOptionsFromModifiers(modifiers: SelectionModifierKeys): SelectNodeCommandArgs["options"] | undefined {
	return modifiers.ctrlKey || modifiers.metaKey || modifiers.shiftKey
		? { multiSelect: true, toggle: true }
		: undefined;
}

/** Keep only selection roots; descendants travel with a selected ancestor. */
export function topLevelHierarchyNodes(nodes: readonly IHierarchyNode[]): IHierarchyNode[] {
	const selected = new Set(nodes);
	return nodes.filter(node => {
		for (let parent = node.parent; parent; parent = parent.parent) {
			if (selected.has(parent)) return false;
		}
		return true;
	});
}


// #region SelectNodeCommand

/**
 * Command to select one or more nodes in the tree.
 * Handles single selection, multi-selection, and deselection.
 */
export class SelectNodeCommand extends UndoableCommand<SelectNodeCommandArgs, { selection: IHierarchyNode[] }> {
	static readonly ID = 'hierarchy.select.node';

	// view-state: selecting must not bury edits on the undo stack
	override readonly pushToUndoStack = false;

	private static STORAGE_ID = 'hierarchy.select.nodes';
	static IS_SELECTED(id: number | string, storage: IStorageService): boolean {
		const prev = storage.getObject(this.STORAGE_ID, StorageScope.WORKSPACE) as Array<number | string> | undefined;
		if (prev?.includes(id)) {
			return true;
		}
		return false;
	}
	private static SET_SELECTED(ids: Array<number | string>, storage: IStorageService): void {
		storage.store(this.STORAGE_ID, ids, StorageScope.WORKSPACE, StorageTarget.USER);
	}

	constructor(
		private readonly prov: IHierarchyService,
		private readonly onSelectedNodesDidChange: Emitter<{ selection: IHierarchyNode[] }>
	) {
		super(SelectNodeCommand.ID, 'Select Node', {

			description: 'Select one or more nodes in the tree!',
			args: {
				name: 'args',
				schema: {
					type: 'object',
					description: 'The arguments for selecting the node',
					properties: {
						target: {
							type: "object",
							description: 'A TARGET returned by hierarchy_search_nodes or property_find, passed back verbatim ({ref?, address?}). Never invent one — search for it.'
						},
						options: {
							type: 'object',
							description: 'Selection options',
							properties: {
								multiSelect: {
									type: 'boolean',
									description: 'Whether to multi-select'
								},
								toggle: {
									type: 'boolean',
									description: 'Whether to toggle selection'
								},
								rangeSelect: {
									type: 'boolean',
									description: 'Whether to select the visible range from the previous anchor'
								}
							}
						}
					}
				}
			}
		});
	}

	private previousSelection: IHierarchyNode[] = [];
	private expandedNodesOnPerform: string[] | null = null;
	private selectionAnchorId: string | null = null;

	canExecute(args: SelectNodeCommandArgs): ICommandCanExecuteReturnValue {
		let { node, options } = args;

		if (!this.prov.findById) {
			return { canExecute: false, reason: 'No tree hierarchy available' };
		}

		// a document target resolves in perform (needs the accessor)
		if (node === undefined && args.target !== undefined) {
			return { canExecute: true };
		}

		if (typeof node === 'string' || typeof node === 'number') {
			node = this.prov.findById(String(node));
			if (!node) {
				return { canExecute: false, reason: 'Node not found' };
			}
		}
		else if (node === null) {
			return { canExecute: true };
		}
		else if (node === undefined) {
			return { canExecute: false, reason: 'Node is undefined' };
		}
		// sometimes AI seems to call { id: 'nodeId' }
		else if ((!isHierarchyNode(node) && "id" in node)) {
			node = this.prov.findById(String((node as any).id));
			if (!node) {
				return { canExecute: false, reason: 'Node not found*' };
			}
		}
		else if (!isHierarchyNode(node)) {
			return { canExecute: false, reason: 'Node is not a valid tree node' };
		}
		return { canExecute: true };
	}

	protected perform(accessor: ServicesAccessor, args: SelectNodeCommandArgs): { selection: IHierarchyNode[] } {

		if (!this.prov) {
			throw new Error('No tree hierarchy available');
		}


		let { node, options } = args;

		// the model-facing shape (1h): resolve the document target to its node
		if (node === undefined && args.target !== undefined) {
			const parsed = parseAiTarget(args.target);
			const targets = accessor.getIfExists(IObjectTargetService) as TargetResolverLike | undefined;
			const owner = parsed ? resolveAiTarget(targets, this.prov, parsed) : null;
			node = owner ? this.prov.find(owner) : null;
			if (!node) {
				throw new Error('Select: the target could not be resolved — call hierarchy_search_nodes for a current target.');
			}
		}

		if (typeof node === 'string' || typeof node === 'number') {
			node = this.prov.findById(String(node));
		}

		const multiSelect = options?.multiSelect ?? false;
		const toggle = options?.toggle ?? false;
		const rangeSelect = options?.rangeSelect ?? false;

		// Store previous selection for undo
		this.previousSelection = Array.from(this.prov.getSelectedNodes());

		let newSelection: IHierarchyNode[];

		if (node) {
			const anchor = this.selectionAnchorId ? this.prov.findById(this.selectionAnchorId) : null;
			if (rangeSelect && anchor) {
				const range = this.prov.getVisibleRange(anchor, node);
				newSelection = range.length > 0 ? range : [node];
			} else if (multiSelect) {
				// Multi-select: add/remove from selection
				if (toggle && node.selected) {
					// Deselect this node
					newSelection = this.previousSelection.filter(n => n !== node);
				} else {
					// Add to selection
					newSelection = [...this.previousSelection, node];
				}
			} else {
				// Single select: replace selection
				if (toggle && node.selected) {
					// Toggle off - deselect all
					newSelection = [];
				} else {
					newSelection = [node];
				}
			}
		}
		else {
			newSelection = [];
		}

		if (!rangeSelect) this.selectionAnchorId = node?.id ?? null;

		// Update all nodes
		for (const n of this.prov.iterateNodes()) {
			n.setWithoutNotify("selected", newSelection.includes(n));
		}
		const res = this.expandSelectionToParents(newSelection);
		this.expandedNodesOnPerform = res ? res.expandedNodes : null;

		// Fire single change event
		this.onSelectedNodesDidChange.fire({ selection: newSelection, active: node ?? undefined });

		// Notify changes
		for (const n of newSelection) {
			n.notifyChanged();
		}
		for (const n of this.previousSelection) {
			if (!newSelection.includes(n)) {
				n.notifyChanged();
			}
		}

		SelectNodeCommand.SET_SELECTED(newSelection.map(n => n.id), accessor.get(IStorageService));
		accessor.get(IStorageService).flush();

		return { selection: newSelection };
	}

	executeForAI(accessor: ServicesAccessor, args: SelectNodeCommandArgs): string {
		const result = this.perform(accessor, args);
		// results carry document targets (1h) — node ids are session-internal
		const targets = accessor.getIfExists(IObjectTargetService) as TargetResolverLike | undefined;
		return safeStringify({
			selected: result.selection.map(s => ({
				name: s.name || s.type,
				target: s.owner && typeof s.owner === "object" ? aiTargetFor(targets, this.prov, s.owner) ?? undefined : undefined,
			})),
		});
	};


	protected undo(accessor: ServicesAccessor): void {

		if (!this.prov) {
			throw new Error('No tree hierarchy available');
		}

		const storage = accessor.get(IStorageService);

		// Restore previous selection
		for (const n of this.prov.iterateNodes()) {
			n.setWithoutNotify("selected", this.previousSelection.includes(n));
		}

		if (this.expandedNodesOnPerform && this.expandedNodesOnPerform.length > 0) {
			for (const node of this.prov.iterateNodes()) {
				if (this.expandedNodesOnPerform.includes(node.id)) {
					node.setWithoutNotify("expanded", false);
						node.notifyChanged(); // mirror perform(): re-render the ancestor as collapsed on undo
				}
			}
		}

		// Notify changes
		for (const n of this.previousSelection) {
			n.notifyChanged();
		}

		this.onSelectedNodesDidChange.fire({ selection: this.previousSelection });

		SelectNodeCommand.SET_SELECTED(this.previousSelection.map(n => n.id), storage);
		storage.flush();
	}

	private expandSelectionToParents(nodes: IHierarchyNode[]): { expandedNodes: string[] } | null {

		let expandedNodeIds: string[] | null = null;

		for (const node of nodes) {
			let current: IHierarchyNode | null = node;
			while (current) {
				if (current.expanded === false && current !== node) {
					current.setWithoutNotify("expanded", true);
						current.notifyChanged(); // re-render ancestor as expanded so the selected descendant mounts and can scroll into view
					expandedNodeIds = expandedNodeIds || [];
					expandedNodeIds.push(current.id);
				}
				current = current.parent;
			}
		}

		return expandedNodeIds ? { expandedNodes: expandedNodeIds } : null;
	}
}



// #region RenameNodeCommand
export interface RenameNodeCommandArgs {
	/** Session entry paths may pass the live row; stored/replayed calls use target. */
	node?: IHierarchyNode;
	target?: OverTarget;
	newName: string;
}

/**
 * Command to rename a node.
 * Syncs the name to the underlying object if it has a name property.
 */
export class RenameNodeCommand extends UndoableCommand<RenameNodeCommandArgs> {
	static readonly ID = 'tree.renameNode';


	constructor(
		private readonly hierarchy: IHierarchyService,
	) {
		super(RenameNodeCommand.ID, 'Rename Node', {
			description: 'Rename a node in the tree',
			args: {
				name: 'args',
				schema: {
					type: 'object',
					description: 'The arguments for renaming the node',
					properties: {
						target: {
							type: 'object',
							description: 'A document target returned by hierarchy_search_nodes or property_find'
						},
						newName: {
							type: 'string',
							description: 'The new name for the node'
						}
					},
					required: ['target', 'newName']
				}
			},
			returns: 'void',
		});
	}

	private oldName: string = '';
	private beforeTarget: OverTarget | null = null;
	private afterTarget: OverTarget | null = null;

	canExecute(args: RenameNodeCommandArgs): ICommandCanExecuteReturnValue {
		const { node, target, newName } = args;

		if (!target && !isHierarchyNode(node)) {
			return { canExecute: false, reason: 'Invalid node' };
		}
		else if (typeof newName !== 'string' || newName.trim().length === 0) {
			return { canExecute: false, reason: 'Invalid new name. The name must be a string > 0' };
		}
		return { canExecute: true };
	}

	protected perform(accessor: ServicesAccessor, args: RenameNodeCommandArgs, context?: ICommandContext): void {
		const { newName } = args;
		const targets = accessor.getIfExists(IObjectTargetService) as IObjectTargetService | undefined;
		const redo = this.emittedOpIds.length > 0;
		const node = this.resolveNode(targets, redo ? this.beforeTarget : args.target, args.node);
		if (!node) throw new Error('Rename: document target could not be resolved to a hierarchy node.');

		if (redo) {
			this.applyName(node, newName);
			return;
		}

		// Store old name for undo
		this.oldName = node.name;
		this.beforeTarget = args.target ?? this.targetFor(targets, node);

		this.applyName(node, newName);
		this.afterTarget = this.targetFor(targets, node) ?? this.beforeTarget;

		// operation seam: a rename is a property edit — without this it never marks the
		// session dirty and is silently LOST on save (GAP 5, plans/project-format-v0.md).
		const object = node.owner;
		if (object && typeof object === "object" && newName !== this.oldName) {
			const operations = accessor.getIfExists(IOperationService);
			// REDO: the base reinstates (H1) — just don't emit a newer op
			if (this.emittedOpIds.length > 0) {
				return;
			}
			// the changeset (live-scene summary) is fed at the command — ops are
			// address-only and no longer carry the live property it reads
			accessor.getIfExists(IChangesetService)?.registerPropertyChange(
				{ key: "name", owner: object, get value() { return (object as { name?: unknown }).name; } },
				this.oldName,
			);
			const target = this.beforeTarget ?? opTargetFor(accessor, object);
			if (!target) return; // warned in opTargetFor — no address, no op
			const op = operations?.emit({
				kind: "set-property",
				target,
				path: "name",
				value: newName,
				previousValue: this.oldName,
			}, originFromContext(context));
			if (op) this.emittedOpIds = [op.id];
		}
	}

	protected undo(accessor: ServicesAccessor, args: RenameNodeCommandArgs): void {
		const targets = accessor.getIfExists(IObjectTargetService) as IObjectTargetService | undefined;
		const node = this.resolveNode(targets, this.afterTarget, args.node);
		if (!node) throw new Error('Rename undo: document target could not be resolved to a hierarchy node.');
		// the LIVE SCENE: restore the name — the base retracts the op (H1)
		this.applyName(node, this.oldName);
	}

	executeForAI(accessor: ServicesAccessor, args: RenameNodeCommandArgs, context?: ICommandContext): string {
		this.execute(accessor, args, context);
		return `Renamed the object to '${args.newName}'.`;
	}

	private targetFor(targets: IObjectTargetService | undefined, node: IHierarchyNode): OverTarget | null {
		const owner = node.owner;
		return owner && typeof owner === "object" ? targets?.targetFor(owner) ?? null : null;
	}

	private resolveNode(targets: IObjectTargetService | undefined, target: OverTarget | null | undefined, fallback?: IHierarchyNode): IHierarchyNode | null {
		if (target && targets) {
			const owner = targets.objectFor(target);
			return owner ? this.hierarchy.find(owner) : null;
		}
		return isHierarchyNode(fallback) ? fallback : null;
	}

	private applyName(node: IHierarchyNode, name: string): void {
		node.setWithoutNotify("name", name);
		const object = node.owner;
		if (object && 'name' in object) {
			(object as any).name = name;
		}
		node.notifyChanged();
	}
}


// #region SetNodeExpandedCommand

export type SetNodeExpandedCommandArgs = {
	node: IHierarchyNode | string;
	expanded: boolean;
	recursive?: boolean;
}

/**
 * Command to toggle node expansion.
 */
export class SetNodeExpandedCommand extends UndoableCommand<SetNodeExpandedCommandArgs> {
	static readonly ID = 'tree.setNodeExpanded';

	// view-state: expanding/collapsing must not bury edits on the undo stack
	override readonly pushToUndoStack = false;


	private static STORAGE_ID = (id: number | string) => 'tree.nodeExpanded.' + id;

	static getStorageState(id: IHierarchyNode, storage: IStorageService): boolean | undefined {
		return storage.getBoolean(SetNodeExpandedCommand.STORAGE_ID(Number(id.id)), StorageScope.WORKSPACE);
	}
	static setStorageState(id: IHierarchyNode, expanded: boolean, storage: IStorageService): void {
		storage.store(SetNodeExpandedCommand.STORAGE_ID(Number(id.id)), expanded, StorageScope.WORKSPACE, StorageTarget.USER);
	}
	static clearStorageState(id: IHierarchyNode, storage: IStorageService): void {
		storage.remove(SetNodeExpandedCommand.STORAGE_ID(Number(id.id)), StorageScope.WORKSPACE);
	}


	constructor(
		private readonly hierarchy: IHierarchyService
	) {
		super(SetNodeExpandedCommand.ID, 'Toggle Expanded');
	}

	canExecute(args: SetNodeExpandedCommandArgs): ICommandCanExecuteReturnValue {
		let { node, expanded } = args;

		if (typeof node === 'string') {
			// Assume string is ID and can be found in the tree
			return { canExecute: true };
		}
		if (!node) {
			return { canExecute: false, reason: 'Node not found' };
		}
		else if (!isHierarchyNode(node)) {
			return { canExecute: false, reason: 'Invalid node' };
		}
		else if (node.expanded === expanded) {
			return { canExecute: false, reason: 'Node already in desired expanded state' };
		}

		return { canExecute: true };
	}

	private _didModifyExpandState: string[] | null = null;

	protected async perform(accessor: ServicesAccessor, args: SetNodeExpandedCommandArgs) {
		const storage = accessor.get(IStorageService);

		let { node, expanded } = args;

		if (typeof node === 'string') {
			const found = this.hierarchy.findById(node);
			if (!found) {
				throw new Error('Node not found');
			}
			node = found;
		}
		if (args.recursive) {
			this._didModifyExpandState = [];
			this.updateExpandedStateRecursively(node, expanded, storage, this._didModifyExpandState);
		}
		else {
			node.setWithoutNotify("expanded", expanded);
			SetNodeExpandedCommand.setStorageState(node, expanded, storage);
		}

		node.notifyChanged();
		storage.flush();
	}

	protected undo(accessor: ServicesAccessor, args: SetNodeExpandedCommandArgs): void {
		const storage = accessor.get(IStorageService);

		let { node, expanded } = args;
		if (typeof node === 'string') {
			const found = this.hierarchy.findById(node);
			if (!found) {
				throw new Error('Node not found');
			}
			node = found;
		}
		node.setWithoutNotify("expanded", !expanded);
		SetNodeExpandedCommand.setStorageState(node, !expanded, storage);

		if (this._didModifyExpandState && this._didModifyExpandState.length > 0) {
			for (const node of this.hierarchy.iterateNodes()) {
				if (this._didModifyExpandState.includes(node.id)) {
					node.setWithoutNotify("expanded", !expanded);
					SetNodeExpandedCommand.setStorageState(node, !expanded, storage);

				}
			}
		}
		node.notifyChanged();
		storage.flush();
	}

	private updateExpandedStateRecursively(node: IHierarchyNode, expanded: boolean, storage: IStorageService, list: string[] | null = null): void {
		node.setWithoutNotify("expanded", expanded);
		SetNodeExpandedCommand.setStorageState(node, expanded, storage);

		list?.push(node.id);
		for (const child of node.children) {
			this.updateExpandedStateRecursively(child, expanded, storage, list);

		}
	}
}

export type DeleteNodeCommandArgs =
	| { node: IHierarchyNode; target?: never }
	| { node?: never; target: OverTarget };


// #region DeleteNodeCommand

/**
 * Command to delete a node from the tree.
 * Stores the node, parent, and index for undo.
 */
export class DeleteNodeCommand extends UndoableCommand<DeleteNodeCommandArgs> {
	static readonly ID = 'tree.deleteNode';

	private deletedNode: IHierarchyNode | null = null;
	private parent: IHierarchyNode | null = null;
	private indexInParent: number = -1;
	private ownerIndex: number = -1;
	private wasSelected: boolean = false;
	private deletedTarget: OverTarget | null = null;
	private parentTarget: OverTarget | null = null;

	constructor(
		private readonly hierarchy: IHierarchyService,
		private readonly onSelectedNodesDidChange: Emitter<{ selection: IHierarchyNode[] }>
	) {
		super(DeleteNodeCommand.ID, 'Delete Node', {
			description: 'Delete a node from the hierarchy',
			args: {
				name: 'args',
				schema: {
					type: 'object',
					description: 'The arguments for deleting the node',
					properties: {
						target: {
							type: 'object',
							description: 'A document target returned by hierarchy_search_nodes or property_find'
						}
					},
					required: ['target']
				}
			},
			returns: 'void',
		});
	}

	canExecute(args: DeleteNodeCommandArgs): ICommandCanExecuteReturnValue {
		if (args.target !== undefined) return { canExecute: true };
		if (!isHierarchyNode(args.node)) {
			return { canExecute: false, reason: 'Invalid node' };
		}
		return { canExecute: true };
	}

	executeForAI(accessor: ServicesAccessor, args: DeleteNodeCommandArgs, context?: ICommandContext): string {
		this.execute(accessor, args, context);
		return "Deleted the object.";
	}

	protected perform(accessor: ServicesAccessor, args: DeleteNodeCommandArgs, context?: ICommandContext): void {
		const logger = accessor.get(ILogService);
		logger.info("Delete node command...");
		const operations = accessor.getIfExists(IOperationService);
		const targets = accessor.getIfExists(IObjectTargetService);

		let node: IHierarchyNode | undefined;
		const target = this.deletedTarget ?? args.target;
		if (target) {
			const owner = targets?.objectFor(target);
			node = owner ? this.hierarchy.find(owner) ?? undefined : undefined;
			if (!node) throw new Error('Delete: the document target could not be resolved to a hierarchy node.');
		}
		else node = args.node;
		if (!node) throw new Error('Delete: no node was provided.');
		this.deletedTarget ??= node.owner && typeof node.owner === "object" ? targets?.targetFor(node.owner) ?? null : null;

		// operation seam FIRST — the fold computes the node's address, which needs the
		// object still ATTACHED to its parent chain (emitted by the COMMAND so every
		// entry path is captured — CLAUDE.md)
		if (node.owner && typeof node.owner === "object") {
			// REDO: the base reinstates (H1) — only a FRESH delete emits
			if (this.emittedOpIds.length === 0) {
				// ADDRESS-ONLY (plans/operation-bridge.md): null = no addressing in
				// this product (chrome) or an unstamped object (warned) — no op
				const target = opTargetFor(accessor, node.owner);
				const op = target ? operations?.emit({ kind: "delete-node", target }, originFromContext(context)) : undefined;
				if (op) this.emittedOpIds = [op.id];
			}
		}

		// Store info for undo — and exempt the node from the hierarchy's
		// deferred disposal of removed nodes: undo re-adds this exact instance.
		this.deletedNode = node;
		internalRetainNodeForUndo(node);
		this.parent = node.parent;
		this.parentTarget = node.parent?.owner && typeof node.parent.owner === "object"
			? targets?.targetFor(node.parent.owner) ?? null
			: null;
		this.indexInParent = node.parent ? node.parent.children.indexOf(node) : -1
		this.wasSelected = node.selected;

		// Deselect if selected
		if (node.selected) {
			node.setWithoutNotify("selected", false);
			const selectedNodes = Array.from(this.hierarchy.getSelectedNodes());
			this.onSelectedNodesDidChange.fire({ selection: selectedNodes });
		}

		// Remove from parent
		// this.hierarchy.remove(node);
		this.hierarchy.remove(node);
		// this.parent?.removeChild(node);

		// The COMMAND owns the owner-graph removal (duck-typed — three's
		// Object3D.removeFromParent). The tree provider deliberately ignores
		// parentless nodes: teardown (playmode swap, project replace, file close)
		// leaves nodes parentless-but-alive in a grace window, and only the command
		// knows this removal is a USER DELETE. Undo relies on the provider's
		// parenting sync: re-adding the node under its parent re-attaches the owner.
		const owner = node.owner as { removeFromParent?: () => void; parent?: { children?: unknown[] } | null } | null | undefined;
		if (owner && typeof owner.removeFromParent === "function") {
			this.ownerIndex = owner.parent?.children?.indexOf(owner) ?? -1;
			owner.removeFromParent();
		}

		node.notifyChanged();

		// Notify changes
		if (this.parent) {
			this.parent.notifyChanged();
		}
	}

	protected undo(accessor: ServicesAccessor, args: DeleteNodeCommandArgs, context?: ICommandContext): void {
		// let { node: targetNode } = node;

		if (!this.deletedNode) {
			console.warn('Cannot undo delete: missing node or parent');
			return;
		}
		const targets = accessor.getIfExists(IObjectTargetService);
		if (this.deletedTarget && targets) {
			const currentOwner = targets.objectFor(this.deletedTarget);
			const currentNode = currentOwner ? this.hierarchy.find(currentOwner) : null;
			if (currentNode) this.deletedNode = currentNode;
		}
		if (this.parentTarget && targets) {
			const currentParentOwner = targets.objectFor(this.parentTarget);
			const currentParent = currentParentOwner ? this.hierarchy.find(currentParentOwner) : null;
			if (!currentParent) throw new Error('Undo delete: the parent target could not be resolved.');
			this.parent = currentParent;
		}

		if (!this.parent) {
			// if the node had no parent, re-add it to the hierarchy roots
			this.hierarchy.add(this.deletedNode);
		}
		// Re-insert at the same index
		else if (this.indexInParent >= 0 && this.indexInParent < this.parent.children.length) {
			this.parent.addChild(this.deletedNode, this.indexInParent);
		} else {
			// Append at the end
			this.parent.addChild(this.deletedNode);
		}

		// The command owns the owner-graph removal (perform), so it restores it
		// SYNCHRONOUSLY too — the provider's parenting sync runs a tick later,
		// which races consumers reading the graph right after undo (the
		// retraction's restore placement).
		const owner = this.deletedNode.owner as { parent?: unknown } | null | undefined;
		const parentOwner = this.parent?.owner as { add?(child: unknown): void; children?: unknown[] } | null | undefined;
		if (owner && parentOwner && typeof parentOwner.add === "function" && owner.parent !== parentOwner) {
			parentOwner.add(owner);
			const children = parentOwner.children;
			if (children && this.ownerIndex >= 0 && this.ownerIndex < children.length - 1) {
				const from = children.indexOf(owner);
				if (from !== -1) {
					children.splice(from, 1);
					children.splice(this.ownerIndex, 0, owner);
				}
			}
		}

		// Restore selection if it was selected
		if (this.wasSelected) {
			this.deletedNode.setWithoutNotify("selected", true);
			const selectedNodes = Array.from(this.hierarchy.getSelectedNodes());
			this.onSelectedNodesDidChange.fire({ selection: selectedNodes });
		}

		this.deletedNode.notifyChanged();
		// the DOCUMENT follows the log: the base retracts the delete after this
		// (H1); the writer supersedes a previously saved active:false over via
		// the fold's retractedTargets
	}

	protected override disposeAction(): void {
		// The undo entry is gone for good. The deleted node was exempted from
		// the hierarchy's deferred disposal (undo re-adds the same instance) —
		// if it wasn't restored, dispose it now so it stops pinning its owner
		// object (and geometry/materials/textures) as a GC root.
		const node = this.deletedNode as HierarchyNode | null;
		if (!node || node.isDisposed) return;
		if (this.hierarchy.findById(node.id) === node) return; // restored via undo
		node.dispose();
	}

	override toAction(service: IInstantiationService, args: DeleteNodeCommandArgs, context?: ICommandContext): IUndoRedoElement {
		// The undo element captures the state on a clone (Object.assign in the
		// base) — clear it off the registry-held SINGLETON so this instance
		// doesn't ALSO retain the deleted node until the next delete.
		const owner = this.deletedNode?.owner;
		const action = super.toAction(service, args, context);
		// history-lifecycle data: when this entry PERMANENTLY leaves history the undo
		// service announces it (onDidDisposeElement) and the fold releases the deleted
		// root's open file — the delete is final, nothing can restore it
		if (owner && typeof owner === "object") {
			(action as { data?: unknown }).data = { kind: "delete-node", owner };
		}
		this.deletedNode = null;
		this.parent = null;
		this.deletedTarget = null;
		this.parentTarget = null;
		return action;
	}
}




// #region NavigateHierarchyCommand

type NavigateHierarchyCommandArgs = {
	direction: 'up' | 'down' | 'left' | 'right';
}

export class NavigateHierarchyCommand extends UndoableCommand<NavigateHierarchyCommandArgs> {

	static readonly ID = 'hierarchy.navigate';

	// view-state: navigating must not bury edits on the undo stack
	override readonly pushToUndoStack = false;

	constructor(
		private readonly hierarchy: IHierarchyService,
		@ILogService private readonly logger: ILogService,
	) {
		super(NavigateHierarchyCommand.ID, 'Navigate the hierarchy tree', {
			description: 'Navigate the hierarchy tree (down/up/left/right)',
		});
	}

	canExecute(args: NavigateHierarchyCommandArgs): ICommandCanExecuteReturnValue {
		return { canExecute: true };
	}

	protected perform(accessor: ServicesAccessor, args: NavigateHierarchyCommandArgs): void {
		this.hierarchy.navigate(args.direction);
	}

	protected undo(accessor: ServicesAccessor, args: NavigateHierarchyCommandArgs): void {
		switch (args.direction) {
			case 'up':
				this.hierarchy.navigate('down');
				break;
			case 'down':
				this.hierarchy.navigate('up');
				break;
			case 'left':
				this.hierarchy.navigate('right');
				break;
			case 'right':
				this.hierarchy.navigate('left');
				break;
		}
	}
}




// #region CopyHierarchyCommand



export interface CopyHierarchyCommandArgs {

}

export class CopyHierarchyCommand extends Command<CopyHierarchyCommandArgs> {

	static readonly ID = 'hierarchy.copy';

	constructor(
	) {
		super(CopyHierarchyCommand.ID, 'Copy Hierarchy to Clipboard', {
			description: 'Copy the entire hierarchy as JSON to the clipboard',
			palette: true,
		});
	}

	canExecute(args: unknown) {
		return { canExecute: true };
	}

	async execute(accessor: ServicesAccessor, args: unknown) {

		const hierarchyService = accessor.get(IHierarchyService);
		const logger = accessor.get(ILogService);
		const clipboard = accessor.get(IClipboardService);
		const notification = accessor.get(INotificationService);
		const telemetry = accessor.get(IAnalyticsService);

		if (!hierarchyService) {
			telemetry.logEvent('hierarchy.copy-json.failed', { reason: 'no hierarchy service available' });
			logger.error('Hierarchy service not available.');
			notification.showNotification({
				type: 'error',
				message: 'Cannot copy: Hierarchy not available.'
			})
			return;
		}

		const serializeNode = (node: IHierarchyNode): any => {
			const obj: any = {
				id: node.id,
				name: node.name,
				type: node.type,
				children: node.children.map(child => serializeNode(child))
			};
			return obj;
		};

		let nodes: IHierarchyNode[] | null = null;
		let showNotification = false;

		if (isContextMenuContext(args)) {
			telemetry.logEvent('hierarchy.copy-json', { source: 'contextMenu' });
			// First try to find a hierarchy node in the targets
			const target = args.targets?.find(t => isHierarchyNode(t));
			if (target && isHierarchyNode(target)) {
				const selection = hierarchyService.getSelectedNodes();
				nodes = target.selected ? topLevelHierarchyNodes(selection) : [target];
				showNotification = true;
			}
			// If no hierarchy node found, check if we have a hierarchy service in the targets (for header context menu)
			else {
				const hierarchyServiceTarget = args.targets?.find(t => t && typeof t === 'object' && 'roots' in t);
				if (hierarchyServiceTarget) {
					// Called from hierarchy header - copy selected nodes or all roots
					nodes = topLevelHierarchyNodes(hierarchyService.getSelectedNodes());
					if (!nodes?.length) nodes = hierarchyService.roots;
					showNotification = true;
				} else {
					logger.error('No valid hierarchy node or service found in context menu targets.');
					notification.showNotification({
						key: 'copyHierarchyNoNode',
						type: 'error',
						message: 'Cannot copy: No valid hierarchy context found.',
						position: 'cursor'
					});
					return;
				}
			}
		}
		else {
			telemetry.logEvent('hierarchy.copy-json', { source: 'commandPalette' });
			nodes = topLevelHierarchyNodes(hierarchyService.getSelectedNodes());
			if (!nodes?.length) nodes = hierarchyService.roots;
		}

		try {
			logger.info(`Copying ${nodes.length} nodes to clipboard.`, nodes.map(n => n.name || n.id));
			const hierarchyObj = nodes.map(root => serializeNode(root));
			const hierarchyJSON = safeStringify(hierarchyObj);
			await clipboard.writeText(hierarchyJSON);
			if (showNotification) {
				notification.showNotification({
					type: 'info',
					message: `Copied ${nodes.length} node(s) to clipboard as JSON.`,
					position: `cursor`,
				});
			}
		}
		catch (err) {
			telemetry.logEvent('hierarchy.copy-json.failed', { reason: 'exception', message: (err instanceof Error ? err.message : String(err)).substring(0, 500) });
			logger.error('Failed to copy hierarchy to clipboard.', err);
			notification.showNotification({
				type: 'error',
				message: 'Failed to copy hierarchy to clipboard: ' + (err instanceof Error ? err.message : String(err)).substring(0, 200),
				position: "toast",
			});
		}
	}
}

CommandsRegistry.registerCommand(new CopyHierarchyCommand());

MenuRegistry.appendMenuItem(MenuId.ExplorerContextCopySubmenu, {
	title: 'Hierarchy',
	submenu: MenuId.ExplorerContextCopyHierarchySubmenu,
	icon: { id: 'copy' },
	order: 10,
	group: '10_clipboard',
})

MenuRegistry.appendMenuItem(MenuId.ExplorerContextCopyHierarchySubmenu, {
	command: {
		id: CopyHierarchyCommand.ID,
		// Just "JSON": the submenu already says Hierarchy, and leaving the full
		// sentence in would read "Copy > Hierarchy > Copy Hierarchy as JSON".
		title: 'JSON',
		icon: { id: 'copy' }
	},
	when: undefined,
	order: 10,
	group: '10_clipboard',
})

MenuRegistry.appendMenuItem(MenuId.HierarchyHeaderContext, {
	command: {
		id: CopyHierarchyCommand.ID,
		title: 'Copy Hierarchy as JSON',
		icon: { id: 'copy' }
	},
	when: undefined,
	order: 10,
	group: '10_clipboard',
})



// #region SearchHierarchyCommand

export interface SearchHierarchyCommandArgs {
	query?: string;
	/** what property_find calls the same thing — accepted, not advertised (see executeForAI) */
	nodeQuery?: string;
	maxResults?: number;
	offset?: number;
	nodeType?: string;
	includeChildren?: boolean;
	/** a TARGET from an earlier result: search only inside that object's subtree
	 * (JSON string tolerated — a model serializes objects) */
	under?: AiTarget | string;
}

export class SearchHierarchyCommand extends Command<SearchHierarchyCommandArgs> {

	static readonly ID = 'hierarchy.search.nodes';

	constructor(
	) {
		super(SearchHierarchyCommand.ID, 'Search Hierarchy Nodes', {
			description: 'Find nodes in the scene by name (case-insensitive, partial match) and optionally by type (regex). Each result carries a TARGET — the document\'s {ref, address} identity for that object. Pass a target back verbatim to property_find / property_read / property.setValue; never build one yourself. Paginated.',
			args: {
				name: 'args',
				schema: {
					type: 'object',
					description: 'The arguments for searching the hierarchy',
					properties: {
						query: {
							type: 'string',
							description: 'The search query. Multiple terms can be separated by commas'
						},
						maxResults: {
							type: 'number',
							description: 'Maximum number of results to return. Default is 100.',
							default: 100
						},
						offset: {
							type: 'number',
							description: 'Number of matching nodes to skip before returning results (for pagination). Default is 0.',
							default: 0
						},
						under: {
							type: 'object',
							description: 'A TARGET from an earlier result: search only inside that object\'s subtree (the object itself included). This is how you ask "every Mesh in this model" — combine it with nodeType and omit query. Without it the search covers the whole scene.'
						},
						nodeType: {
							type: 'string',
							description: 'Filter by node type as a case-insensitive regex — "Mesh", "Light.*", "Mesh|Camera". NOTE: this filters the nodes the name query already matched, so pairing it with an unrelated query ({query: "Sketchfab", nodeType: "Mesh"}) finds nothing. To list by type, omit query or pass "*".'
						},
						includeChildren: {
							type: 'boolean',
							description: 'Whether to include information about child nodes. Default is false.',
							default: false
						}
					},
					required: [],
				}
			},
		});
	}

	canExecute(args: unknown) {
		return { canExecute: true };
	}

	async execute(accessor: ServicesAccessor, args: SearchHierarchyCommandArgs): Promise<Array<IHierarchyNode>> {
		const hierarchyService = accessor.get(IHierarchyService);
		const logger = accessor.get(ILogService);

		if (!hierarchyService) {
			logger.error('Hierarchy service not available.');
			return [];
		}

		if(!args.query) {
			throw new Error('Search query is required.');
		}

		const query = args.query.split(',').map(q => q.trim()).filter(q => q.length > 0);
		const maxResults = args.maxResults || 100;
		const offset = args.offset || 0;
		const nodeTypeRegex = args.nodeType ? new RegExp(args.nodeType, "i") : null;

		// Search with a higher limit to account for filtering and offset
		const searchLimit = nodeTypeRegex ? maxResults * 3 : maxResults + offset;
		let allResults = await hierarchyService.search(query, searchLimit);

		// Apply type filter if specified
		if (nodeTypeRegex) {
			allResults = allResults.filter(n => n.type && nodeTypeRegex.test(n.type));
		}

		// Apply pagination
		const results = allResults.slice(offset, offset + maxResults);

		logger.info(`Search for "${args.query}" found ${allResults.length} result(s), returning ${results.length} after pagination.`);
		return results;
	}

	/** Unnamed three.js objects have an empty `name` — fall back to the label,
	 *  then the type, so a caller can still refer to the node. */
	private static displayName(n: { name: string; label: string; type: string }): string {
		return n.name || n.label || n.type;
	}

	/** ONE answer shape for both search paths — a subtree result must look exactly
	 *  like a whole-tree result or the caller has to learn two formats. */
	private static formatNode(n: IHierarchyNode, includeChildren: boolean, mint: (object: object) => AiTarget | null): Record<string, unknown> {
		const nodeData: any = {
			// THE identity token (1h): the document target, passed verbatim to the
			// property tools. Session handles and node ids never reach the model.
			target: n.owner && typeof n.owner === "object" ? mint(n.owner) ?? undefined : undefined,
			name: SearchHierarchyCommand.displayName(n),
			type: n.type,
			isSelected: n.selected,
			isVisible: n.visible,
			isExpanded: n.expanded,
			childrenCount: n.children.length
		};

		if (includeChildren && n.children.length > 0) {
			nodeData.children = n.children.map(child => ({
				target: child.owner && typeof child.owner === "object" ? mint(child.owner) ?? undefined : undefined,
				name: SearchHierarchyCommand.displayName(child),
				type: child.type
			}));
		}

		if (n.searchTags && n.searchTags.length > 0) {
			nodeData.tags = n.searchTags;
		}

		return nodeData;
	}

	/**
	 * Everything inside one object, filtered locally.
	 *
	 * Whole-tree search cannot answer "every Mesh under this model": it matches
	 * name and type globally, so the caller had to walk down one level per call,
	 * learning the next name from the previous answer. Seven calls for two meshes.
	 */
	private searchSubtree(hierarchy: IHierarchyService, targets: TargetResolverLike | undefined, args: SearchHierarchyCommandArgs, terms: string[]): string {
		// tolerate a JSON-stringified target — a model serializes objects
		const under = parseAiTarget(args.under);
		const owner = under ? resolveAiTarget(targets, hierarchy, under) : null;
		const root = owner ? hierarchy.find(owner) : null;
		if (!root) {
			return safeStringify({
				error: `The 'under' target could not be resolved (${safeStringify(args.under)})`,
				hint: "Targets come from this tool, resources_list or property_find — pass one back verbatim.",
				results: [],
				totalReturned: 0,
				totalMatched: 0,
				hasMore: false,
			});
		}

		const typeRegex = args.nodeType ? new RegExp(args.nodeType, "i") : null;
		const wantsAll = terms.includes("*");
		const matched: IHierarchyNode[] = [];
		// the root counts: "under Sketchfab_model" including the model itself is
		// what a person means, and excluding it surprises nobody usefully
		const visit = (node: IHierarchyNode) => {
			const nameMatch = wantsAll || terms.some(term =>
				node.name?.toLowerCase().includes(term.toLowerCase())
				|| node.type?.toLowerCase().includes(term.toLowerCase()));
			if (nameMatch && (!typeRegex || (node.type && typeRegex.test(node.type)))) {
				matched.push(node);
			}
			for (const child of node.children) visit(child);
		};
		visit(root);

		const maxResults = args.maxResults || 100;
		const offset = args.offset || 0;
		const page = matched.slice(offset, offset + maxResults);
		const mint = (object: object) => aiTargetFor(targets, hierarchy, object);
		return safeStringify({
			totalReturned: page.length,
			totalMatched: matched.length,
			hasMore: matched.length > offset + page.length,
			pagination: { offset, limit: maxResults },
			filters: { under: args.under, query: args.query ?? args.nodeQuery, nodeType: args.nodeType, includeChildren: args.includeChildren ?? false },
			results: page.map(node => SearchHierarchyCommand.formatNode(node, args.includeChildren ?? false, mint)),
		});
	}

	async executeForAI(accessor: ServicesAccessor, args: SearchHierarchyCommandArgs) {
		const hierarchyService = accessor.get(IHierarchyService);
		// undefined outside the document products (chrome) — the target seam
		// degrades to ref-less graph addresses there
		const targetService = accessor.getIfExists(IObjectTargetService) as TargetResolverLike | undefined;
		if (!hierarchyService) {
			return JSON.stringify({
				error: "Hierarchy service not available",
				results: []
			});
		}

		// query is OPTIONAL: "*" matches everything, so "every Mesh" is askable. It
		// was required, and the wildcard undocumented — a chat session guessed "."
		// twice, got nothing, and gave up on finding the model's meshes.
		//
		// `nodeQuery` is property_find's name for the same parameter (that tool also
		// takes a propertyQuery, so a bare "query" would be ambiguous there). Sent
		// here it was dropped, the search fell through to the wildcard, and one
		// lookup answered with the whole tree — 33k characters.
		const rawQuery = args.query ?? args.nodeQuery;
		const query = rawQuery?.split(',').map(q => q.trim()).filter(q => q.length > 0);
		const terms = query?.length ? query : ["*"];
		const maxResults = args.maxResults || 100;
		const offset = args.offset || 0;

		// "every Mesh UNDER this model" — the question a person asks. Without it,
		// finding the meshes inside a selected model took seven calls walking one
		// level per answer.
		if (args.under) {
			return this.searchSubtree(hierarchyService, targetService, args, terms);
		}
		const nodeTypeRegex = args.nodeType ? new RegExp(args.nodeType, "i") : null;
		const includeChildren = args.includeChildren ?? false;

		// Search with a higher limit to account for filtering and offset
		const searchLimit = nodeTypeRegex ? maxResults * 3 : maxResults + offset;
		let allResults = await hierarchyService.search(terms, searchLimit);

		// Apply type filter if specified
		if (nodeTypeRegex) {
			allResults = allResults.filter(n => n.type && nodeTypeRegex.test(n.type));
		}

		const totalMatched = allResults.length;

		// Apply pagination
		const results = allResults.slice(offset, offset + maxResults);

		// Unnamed three.js objects have an empty `name` — fall back to the
		// label, then the type, so AI clients can still refer to the node
		// (an all-empty `name` column made results uncorrelatable).
		const mint = (object: object) => aiTargetFor(targetService, hierarchyService, object);
		const formattedResults = results.map(n => SearchHierarchyCommand.formatNode(n, includeChildren, mint));

		return safeStringify({
			totalReturned: results.length,
			totalMatched: totalMatched,
			hasMore: totalMatched > (offset + results.length),
			pagination: {
				offset,
				limit: maxResults
			},
			filters: {
				query: rawQuery,
				nodeType: args.nodeType,
				includeChildren
			},
			results: formattedResults,
		});
	}
}

CommandsRegistry.registerCommand(new SearchHierarchyCommand());

// #region CopyTransformCommands

/**
 * "Copy > Position / Rotation / Matrix" on a tree node.
 *
 * One command per format because a menu item carries nothing but a command id
 * (IMenuItem has no args field), so the format cannot be passed in — the shape
 * below keeps the logic in ONE place regardless.
 *
 * Values are read from the node's `owner`, which for the three provider is an
 * Object3D. Duck-typed, never `instanceof`: in the chrome extension the object
 * belongs to the INSPECTED PAGE's realm, where our three's classes are different
 * constructors and every `instanceof` is false.
 *
 * World-space values are decomposed from `matrixWorld` with OUR three (operating
 * on plain numbers copied out of the page's matrix), rather than calling the
 * page object's own getWorld* methods, which expect a target of their own realm.
 */
type TransformFormat = {
	readonly id: string;
	/** Menu text. Short, because the submenu already says Position/Rotation/... */
	readonly title: string;
	/** Standalone name, for the command palette and notifications, where there is
	 *  no surrounding submenu to supply the context ("Copied Local" means nothing). */
	readonly label: string;
	/** null when the object cannot supply this value (e.g. no matrixWorld yet). */
	read(owner: Record<string, any>): string | null;
};

/** Is this a three Object3D, as far as we can tell across realms? */
function hasTransform(owner: unknown): owner is Record<string, any> {
	if (!owner || typeof owner !== "object") return false;
	const o = owner as Record<string, any>;
	return o.isObject3D === true || (!!o.position && !!o.quaternion);
}

/** World position/rotation, decomposed from the object's world matrix. */
function decomposeWorld(owner: Record<string, any>): { position: Vector3; quaternion: Quaternion } | null {
	const elements = owner.matrixWorld?.elements;
	if (!elements || elements.length !== 16) return null;
	const matrix = new Matrix4().fromArray(Array.from(elements as ArrayLike<number>));
	const position = new Vector3();
	const quaternion = new Quaternion();
	matrix.decompose(position, quaternion, new Vector3());
	return { position, quaternion };
}

const TRANSFORM_FORMATS: readonly (TransformFormat & { menu: MenuId })[] = [
	{
		id: "hierarchy.copyTransform.position",
		title: "Local",
		label: "Position (Local)",
		menu: MenuId.ExplorerContextCopyPositionSubmenu,
		read: o => (o.position ? formatVector3(o.position) : null),
	},
	{
		id: "hierarchy.copyTransform.positionWorld",
		title: "World",
		label: "Position (World)",
		menu: MenuId.ExplorerContextCopyPositionSubmenu,
		read: o => {
			const world = decomposeWorld(o);
			return world ? formatVector3(world.position) : null;
		},
	},
	{
		id: "hierarchy.copyTransform.positionUnity",
		title: "Unity Vector3 (World)",
		label: "Position as Unity Vector3 (World)",
		menu: MenuId.ExplorerContextCopyPositionSubmenu,
		read: o => {
			const world = decomposeWorld(o);
			return world ? formatUnityVector3(world.position) : null;
		},
	},
	{
		id: "hierarchy.copyTransform.rotationEuler",
		title: "Euler (Local)",
		label: "Rotation Euler (Local)",
		menu: MenuId.ExplorerContextCopyRotationSubmenu,
		read: o => (o.rotation ? formatEulerDegrees(o.rotation) : null),
	},
	{
		id: "hierarchy.copyTransform.rotationEulerWorld",
		title: "Euler (World)",
		label: "Rotation Euler (World)",
		menu: MenuId.ExplorerContextCopyRotationSubmenu,
		read: o => {
			const world = decomposeWorld(o);
			if (!world) return null;
			return formatEulerDegrees(new Euler().setFromQuaternion(world.quaternion, "XYZ"));
		},
	},
	{
		id: "hierarchy.copyTransform.rotationQuaternion",
		title: "Quaternion (Local)",
		label: "Rotation Quaternion (Local)",
		menu: MenuId.ExplorerContextCopyRotationSubmenu,
		read: o => (o.quaternion ? formatQuaternion(o.quaternion) : null),
	},
	{
		id: "hierarchy.copyTransform.rotationQuaternionWorld",
		title: "Quaternion (World)",
		label: "Rotation Quaternion (World)",
		menu: MenuId.ExplorerContextCopyRotationSubmenu,
		read: o => {
			const world = decomposeWorld(o);
			return world ? formatQuaternion(world.quaternion) : null;
		},
	},
	{
		id: "hierarchy.copyTransform.scale",
		title: "Local",
		label: "Scale (Local)",
		menu: MenuId.ExplorerContextCopyScaleSubmenu,
		read: o => (o.scale ? formatVector3(o.scale) : null),
	},
	{
		id: "hierarchy.copyTransform.scaleWorld",
		title: "World (Lossy)",
		label: "Scale (World, Lossy)",
		menu: MenuId.ExplorerContextCopyScaleSubmenu,
		// "Lossy" is Unity's own name for this: a world scale decomposed from a
		// matrix cannot represent shear, so under a sheared parent it is an
		// approximation. Naming it stops the number being trusted too far.
		read: o => {
			const elements = o.matrixWorld?.elements;
			if (!elements || elements.length !== 16) return null;
			const scale = new Vector3();
			new Matrix4().fromArray(Array.from(elements as ArrayLike<number>))
				.decompose(new Vector3(), new Quaternion(), scale);
			return formatVector3(scale);
		},
	},
	{
		id: "hierarchy.copyTransform.matrix",
		title: "Local",
		label: "Matrix (Local)",
		menu: MenuId.ExplorerContextCopyMatrixSubmenu,
		read: o => (o.matrix?.elements ? formatMatrix4(Array.from(o.matrix.elements as ArrayLike<number>)) : null),
	},
	{
		id: "hierarchy.copyTransform.matrixWorld",
		title: "World",
		label: "Matrix (World)",
		menu: MenuId.ExplorerContextCopyMatrixSubmenu,
		read: o => (o.matrixWorld?.elements ? formatMatrix4(Array.from(o.matrixWorld.elements as ArrayLike<number>)) : null),
	},
];

/** Entries that sit directly in the Copy menu rather than a transform submenu. */
const TRANSFORM_TOP_LEVEL: readonly TransformFormat[] = [
	{
		id: "hierarchy.copyTransform.everything",
		title: "Transform (TRS)",
		label: "Transform (TRS)",
		read: o => {
			if (!o.position || !o.rotation || !o.quaternion || !o.scale) return null;
			return formatTransformBlock({
				position: o.position,
				rotationEuler: o.rotation,
				quaternion: o.quaternion,
				scale: o.scale,
			});
		},
	},
	{
		id: "hierarchy.copyTransform.unity",
		title: "Unity Transform",
		label: "Unity Transform",
		// Unity's clipboard key is TransformWorldPlacementJSON: it pastes WORLD
		// placement, so local values would land the object in the wrong place for
		// anything parented.
		read: o => {
			const elements = o.matrixWorld?.elements;
			if (!elements || elements.length !== 16) return null;
			const position = new Vector3();
			const rotation = new Quaternion();
			const scale = new Vector3();
			new Matrix4().fromArray(Array.from(elements as ArrayLike<number>)).decompose(position, rotation, scale);
			return formatUnityTransformClipboard({ position, rotation, scale });
		},
	},
];

class CopyTransformCommand extends Command<{}> {

	constructor(private readonly format: TransformFormat) {
		super(format.id, `Copy ${format.label}`, {
			description: `Copy the selected object's ${format.label.toLowerCase()} to the clipboard`,
			palette: true,
		});
	}

	canExecute() {
		return { canExecute: true };
	}

	async execute(accessor: ServicesAccessor, args: unknown) {
		const hierarchyService = accessor.get(IHierarchyService);
		const logger = accessor.get(ILogService);
		const clipboard = accessor.get(IClipboardService);
		const notification = accessor.get(INotificationService);
		const telemetry = accessor.get(IAnalyticsService);

		// Context menu targets first, falling back to the selection so the same
		// command works from the palette (mirrors CopyHierarchyCommand).
		let node: IHierarchyNode | null = null;
		if (isContextMenuContext(args)) {
			const target = args.targets?.find(t => isHierarchyNode(t));
			if (target && isHierarchyNode(target)) node = target;
		}
		if (!node) node = hierarchyService?.getSelectedNodes()?.[0] ?? null;

		const owner = node?.owner;
		if (!node || !hasTransform(owner)) {
			notification.showNotification({
				type: 'info',
				message: 'Select an object with a transform first.',
				position: 'cursor',
			});
			return;
		}

		let text: string | null = null;
		try {
			text = this.format.read(owner as Record<string, any>);
		}
		catch (err) {
			logger.error(`Failed to read ${this.format.label}`, err);
		}
		if (text === null) {
			telemetry.logEvent('hierarchy.copy-transform.failed', { format: this.format.id, reason: 'unavailable' });
			notification.showNotification({
				type: 'info',
				message: `This object has no ${this.format.label.toLowerCase()}.`,
				position: 'cursor',
			});
			return;
		}

		try {
			await clipboard.writeText(text);
			telemetry.logEvent('hierarchy.copy-transform', { format: this.format.id });
			notification.showNotification({
				type: 'info',
				// The value itself is deliberately NOT echoed: a matrix is four
				// lines and a Unity transform is a ~200 character blob, which
				// buries the toast and tells the user nothing they did not just
				// ask for. `label`, not `title` — the menu title is "Local"
				// inside its submenu, which alone means nothing in a toast.
				message: `Copied ${this.format.label}`,
				position: 'cursor',
			});
		}
		catch (err) {
			telemetry.logEvent('hierarchy.copy-transform.failed', { format: this.format.id, reason: 'clipboard' });
			logger.error('Failed to copy transform to clipboard.', err);
			notification.showNotification({
				type: 'error',
				message: 'Failed to copy to clipboard: ' + (err instanceof Error ? err.message : String(err)).substring(0, 200),
				position: 'toast',
			});
		}
	}
}

for (const [index, entry] of [
	{ title: 'Position', submenu: MenuId.ExplorerContextCopyPositionSubmenu },
	{ title: 'Rotation', submenu: MenuId.ExplorerContextCopyRotationSubmenu },
	{ title: 'Scale', submenu: MenuId.ExplorerContextCopyScaleSubmenu },
	{ title: 'Matrix', submenu: MenuId.ExplorerContextCopyMatrixSubmenu },
].entries()) {
	MenuRegistry.appendMenuItem(MenuId.ExplorerContextCopySubmenu, {
		title: entry.title,
		submenu: entry.submenu,
		icon: { id: 'copy' },
		order: 20 + index,
		group: '20_transform',
	});
}

TRANSFORM_FORMATS.forEach((format, index) => {
	CommandsRegistry.registerCommand(new CopyTransformCommand(format));
	MenuRegistry.appendMenuItem(format.menu, {
		command: { id: format.id, title: format.title, icon: { id: 'copy' } },
		when: undefined,
		order: index,
		group: '10_values',
	});
});

TRANSFORM_TOP_LEVEL.forEach((format, index) => {
	CommandsRegistry.registerCommand(new CopyTransformCommand(format));
	MenuRegistry.appendMenuItem(MenuId.ExplorerContextCopySubmenu, {
		command: { id: format.id, title: format.title, icon: { id: 'copy' } },
		when: undefined,
		order: 30 + index,
		group: '30_transform_all',
	});
});


// #region selection shortcuts (Delete / Ctrl+D anywhere)

/**
 * Entry path for the DELETE key anywhere in the editor — viewport included:
 * an object picked in the 3D scene is SELECTED, and selection is the one
 * source of truth, so this reads IHierarchyService.getSelectedNodes() and
 * routes every node through tree.deleteNode. Not undoable itself: the
 * per-node commands push their entries, and the grouping window folds a
 * multi-select delete into one undo gesture. canExecute doubles as the
 * key-collision guard (allowOnCanvas): with nothing selected the key stays
 * with the host page.
 */
export class DeleteSelectedNodesCommand implements ICommand<void, Promise<void>> {
	static readonly ID = "hierarchy.deleteSelected";
	readonly id = DeleteSelectedNodesCommand.ID;
	readonly label = "Delete Selection";
	readonly metadata = { description: "Delete all currently selected objects", palette: true };

	constructor(private readonly hierarchy: IHierarchyService) { }

	canExecute(): ICommandCanExecuteReturnValue {
		if (this.hierarchy.getSelectedNodes().length === 0) {
			return { canExecute: false, reason: "Nothing is selected" };
		}
		return { canExecute: true };
	}

	async execute(accessor: ServicesAccessor, _args: void, context?: ICommandContext): Promise<void> {
		const commands = accessor.get(ICommandService);
		// snapshot: each delete mutates the selection under the loop
		const selection = topLevelHierarchyNodes(this.hierarchy.getSelectedNodes());
		for (const node of selection) {
			await commands.executeCommand<DeleteNodeCommand>(DeleteNodeCommand.ID, { node }, context as ICommandContextUser | undefined);
		}
	}
}

/**
 * Ctrl+D — duplicate the selection, same shape as {@link DeleteSelectedNodesCommand}.
 * The product-specific command ("hierarchy.duplicate") is referenced by id so
 * this platform module does not import a clone implementation. Products that do
 * not register it leave Ctrl+D with the host page.
 */
export class DuplicateSelectedNodesCommand implements ICommand<unknown, Promise<void>> {
	static readonly ID = "hierarchy.duplicateSelected";
	/** the needle module's DuplicateNodeCommand.ID — by string, so this file
	 * (platform) never imports editor/needle code */
	static readonly PER_NODE_ID = "hierarchy.duplicate";
	readonly id = DuplicateSelectedNodesCommand.ID;
	readonly label = "Duplicate Selection";
	readonly metadata = { description: "Duplicate all currently selected objects", palette: true };

	constructor(private readonly hierarchy: IHierarchyService) { }

	canExecute(args?: unknown): ICommandCanExecuteReturnValue {
		if (!CommandsRegistry.getCommand(DuplicateSelectedNodesCommand.PER_NODE_ID)) {
			return { canExecute: false, reason: "This product cannot duplicate objects" };
		}
		if (this.nodesFor(args).length === 0) {
			return { canExecute: false, reason: "Nothing is selected" };
		}
		return { canExecute: true };
	}

	private nodesFor(args: unknown): IHierarchyNode[] {
		const contextTargets = (args as { targets?: unknown[] } | undefined)?.targets;
		if (Array.isArray(contextTargets)) {
			const target = contextTargets.find(candidate => isHierarchyNode(candidate));
			if (target && isHierarchyNode(target) && !target.selected) return [target];
		}
		return topLevelHierarchyNodes(this.hierarchy.getSelectedNodes());
	}

	async execute(accessor: ServicesAccessor, args: unknown, context?: ICommandContext): Promise<void> {
		const commands = accessor.get(ICommandService);
		const selection = this.nodesFor(args);
		const commandArgs = selection.length === 1 ? { node: selection[0], select: false } : { nodes: selection, select: false };
		const outcome = await commands.executeCommand(DuplicateSelectedNodesCommand.PER_NODE_ID, commandArgs, context as ICommandContextUser | undefined) as {
			success: boolean;
			result?: IHierarchyNode | readonly IHierarchyNode[] | null;
		};
		const copies = outcome.success
			? Array.isArray(outcome.result) ? [...outcome.result] : outcome.result ? [outcome.result as IHierarchyNode] : []
			: [];
		for (let index = 0; index < copies.length; index++) {
			await commands.executeCommand<SelectNodeCommand>(SelectNodeCommand.ID, {
				node: copies[index],
				...(index > 0 ? { options: { multiSelect: true } } : {}),
			});
		}
	}
}

// #endregion
